import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  LoadingScreen,
} from "@roberttamas/ui";
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { AppShell } from "@/components/app-shell";
import { useAuth } from "@/lib/auth";
import { saveBuyIntent } from "@/lib/buy-intent";
import { formatAmount } from "@/lib/pricing";
import { usePublishedPricing } from "@/lib/pricing-public";
import type { PricingTier } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * Buy entry point.
 *
 * `/buy?tier=<id>&preset=<id>&locale=<cs|en>` — public: landing-page CTAs
 * hit this pre-login, with a tier already picked, so it skips straight
 * through to signup/checkout without rendering anything.
 *
 * `/buy` with no `tier` — signed-in only, reached from the app's own
 * "Buy" nav item — shows a picker over every published tier (e.g. the
 * plain event page and the +photos upload tiers), since there's no
 * landing-page context to infer a choice from. A signed-out visitor here
 * is sent straight to login; the picker itself is never public.
 */
export function BuyPage() {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const pricing = usePublishedPricing();

  const requestedTierId = params.get("tier");
  // Landing-page deep links pass the visitor's chosen locale explicitly;
  // otherwise (the signed-in "Buy" nav item) follow the admin UI's language,
  // so the tier copy matches what the buyer already sees on screen.
  const paramLocale = params.get("locale");
  const locale =
    paramLocale === "en" || paramLocale === "cs"
      ? paramLocale
      : (i18n.resolvedLanguage ?? "en");

  const proceed = (tierId: string, presetId: string | null) => {
    saveBuyIntent({ tierId, presetId, locale });
    if (user) {
      navigate("/checkout", { replace: true });
    } else {
      navigate("/login?mode=signup", {
        replace: true,
        state: { from: { pathname: "/checkout" } },
      });
    }
  };

  useEffect(() => {
    if (loading) return;

    if (!requestedTierId) {
      // No tier param, and not the landing-page flow — this view is for
      // signed-in buyers only. Anonymous visitors go straight to login
      // instead of ever seeing the picker.
      if (!user) {
        navigate("/login?mode=signup", {
          replace: true,
          state: { from: { pathname: "/buy" } },
        });
      }
      return;
    }

    if (pricing.isLoading) return;

    const tiers = pricing.data?.tiers ?? [];
    const tier = tiers.find((t) => t.id === requestedTierId);

    if (tier) {
      let presetId: string | null = null;
      if (tier.presets && tier.presets.length > 0) {
        const preset =
          tier.presets.find((p) => p.id === params.get("preset")) ??
          tier.presets.find((p) => p.isDefault) ??
          tier.presets[0];
        presetId = preset?.id ?? null;
      }
      proceed(tier.id, presetId);
    } else {
      // Pricing unavailable or unknown tier id — keep the raw selection;
      // checkout revalidates against fresh pricing anyway.
      proceed(requestedTierId, params.get("preset"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user, pricing.isLoading, pricing.data, requestedTierId]);

  // Deep link from the landing page, still resolving auth/pricing, or a
  // signed-out visitor being sent to login — nothing to render in any
  // of these cases (the picker below is reachable only once `user` is
  // confirmed signed in).
  if (requestedTierId || loading || !user || pricing.isLoading) {
    return <LoadingScreen />;
  }

  // Reached via the sidebar "Buy" nav item — show the picker inside the
  // usual dashboard shell rather than a bare page.
  const wrap = (node: ReactNode) => <AppShell>{node}</AppShell>;

  if (pricing.isError || !pricing.data || pricing.data.tiers.length === 0) {
    return wrap(
      <div className="mx-auto max-w-2xl py-10">
        <Card>
          <CardContent className="pt-6 text-sm text-muted-foreground">
            {t("buy.unavailable")}
          </CardContent>
        </Card>
      </div>,
    );
  }

  return wrap(
    <TierPicker
      tiers={pricing.data.tiers}
      locale={locale}
      currency={
        pricing.data.config.localeCurrency[locale] ??
        pricing.data.config.localeCurrency[pricing.data.config.defaultLocale] ??
        pricing.data.config.currencies[0] ??
        "CZK"
      }
      onChoose={proceed}
    />,
  );
}

function TierPicker({
  tiers,
  locale,
  currency,
  onChoose,
}: {
  tiers: PricingTier[];
  locale: string;
  currency: string;
  onChoose: (tierId: string, presetId: string | null) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="mx-auto max-w-4xl space-y-6 py-6">
      <div>
        <h1 className="text-2xl font-semibold">{t("buy.chooseTitle")}</h1>
        <p className="text-sm text-muted-foreground">{t("buy.chooseSubtitle")}</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {tiers.map((tier) => (
          <TierCard
            key={tier.id}
            tier={tier}
            locale={locale}
            currency={currency}
            onChoose={onChoose}
          />
        ))}
      </div>
    </div>
  );
}

function TierCard({
  tier,
  locale,
  currency,
  onChoose,
}: {
  tier: PricingTier;
  locale: string;
  currency: string;
  onChoose: (tierId: string, presetId: string | null) => void;
}) {
  const { t } = useTranslation();
  const copy = tier.copy[locale] ?? Object.values(tier.copy)[0];
  const presets = tier.presets ?? [];

  const [presetId, setPresetId] = useState<string | null>(
    () => presets.find((p) => p.isDefault)?.id ?? presets[0]?.id ?? null,
  );

  const selectedPreset = presets.find((p) => p.id === presetId) ?? null;

  const amountMinor = selectedPreset
    ? selectedPreset.prices[currency]
    : tier.prices?.[currency];

  return (
    <Card className={cn(tier.highlighted && "border-primary")}>
      <CardHeader className="space-y-1">
        {copy?.badge && <Badge className="w-fit">{copy.badge}</Badge>}
        <CardTitle className="text-lg">{copy?.name ?? tier.id}</CardTitle>
        {copy?.tagline && (
          <p className="text-sm text-muted-foreground">{copy.tagline}</p>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-2xl font-semibold">
          {amountMinor !== undefined ? formatAmount(amountMinor, currency) : "-"}
        </p>

        {presets.length > 1 && (
          <div
            className="grid gap-1 rounded-md border p-1"
            style={{ gridTemplateColumns: `repeat(${presets.length}, minmax(0, 1fr))` }}
            role="group"
            aria-label={copy?.legend ?? t("buy.packageSizeAria")}
          >
            {presets.map((p) => {
              const active = p.id === presetId;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPresetId(p.id)}
                  aria-pressed={active}
                  className={cn(
                    "rounded px-2 py-1.5 text-sm font-medium transition-colors",
                    active
                      ? "bg-accent text-accent-foreground"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {p.copy[locale]?.pill ?? `${p.storageGb} GB`}
                </button>
              );
            })}
          </div>
        )}

        {copy?.features && copy.features.length > 0 && (
          <ul className="space-y-1.5 text-sm">
            {copy.features.map((feature) => (
              <li key={feature} className="flex gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        )}

        <Button
          className="w-full"
          variant={tier.highlighted ? "default" : "outline"}
          onClick={() => onChoose(tier.id, presetId)}
        >
          {copy?.cta || t("buy.choose")}
        </Button>
      </CardContent>
    </Card>
  );
}
